import React from "react";
import { Link } from "react-router-dom";
import { Priceable } from "../../Types";
import { Quotation } from "../../../../stores/CoursePreviewStore/Types";
import { monthsSaved, savingPercent } from "../../service";
import { monthsFree } from "../../../../utils/helpers";
import LifetimeSubscription from "./LifetimeSubscription";

interface Props {
  priceable: Priceable;
  slug: string;
  quotes?: Quotation[];
  activeQuote?: Quotation;
  communityUpgradeAmount?: string;
  canChangeSubscription?: boolean;
}

const Subscription: React.FC<Props> = ({
  priceable,
  slug,
  quotes,
  activeQuote,
  communityUpgradeAmount,
  canChangeSubscription = true,
}) => {
  const isCommunity = slug === "community" || priceable?.is_community;
  const isLifetime = slug === "lifetime" || priceable?.is_lifetime;
  const preventDisabledNavigation = (e) => {
    if (!canChangeSubscription) e.preventDefault();
  };
  const optionClassName = (isActive) =>
    `${isActive ? "active" : ""} ${
      canChangeSubscription ? "" : "click-disabled"
    }`.trim();

  // The yearly quote is the one that carries the pay-in-full saving. On the
  // monthly page it sits alongside the active quote; on the yearly page it IS
  // the active quote, and the only one there is.
  const yearlyQuote =
    quotes?.find((quote) => quote?.interval === "annually") ||
    (activeQuote?.interval === "annually" ? activeQuote : undefined);
  const upfrontSaving = savingPercent(yearlyQuote);
  const showUpfrontSaving = Boolean(
    yearlyQuote?.discounted && upfrontSaving > 0,
  );
  const isPayingInFull = priceable?.interval === "year";
  // Paying the year upfront is offered as months free. Anything else (a
  // voucher on top, say) is described in money, as before.
  const freeMonths = showUpfrontSaving ? monthsSaved(yearlyQuote) : 0;

  return (
    <>
      {isLifetime ? (
        <LifetimeSubscription forMember={Boolean(priceable?.discounted)} />
      ) : isCommunity ? (
        <p className="subtitle" id="formSubtitle">
          Join 16,000+ marketers, designers and curious minds. About the price
          of two coffees a month.
        </p>
      ) : (
        <p className="subtitle" id="formSubtitle">
          You're moments away from 40+ properly good courses.
        </p>
      )}

      {isLifetime ? null : isCommunity ? (
        communityUpgradeAmount && (
          <div
            className="upgrade-nudge"
            id="upgradeNudge"
            style={{ display: "flex" }}
          >
            <p id="upgradeNudgeCopy">
              For only <strong>{communityUpgradeAmount} more a month</strong>,
              get all 40+ Mini MBA courses.
            </p>
            <Link
              to="/passes/month/pay"
              className="nudge-btn"
              id="upgradeNudgeBtn"
              onClick={preventDisabledNavigation}
              aria-disabled={!canChangeSubscription}
              tabIndex={canChangeSubscription ? undefined : -1}
            >
              See All Courses Pass
            </Link>
          </div>
        )
      ) : (
        <>
          <div
            className="payment-toggle"
            id="payToggle"
            style={{ display: "flex" }}
            role="tablist"
            aria-label="Payment frequency"
          >
            <Link
              to="/passes/month/pay"
              className={optionClassName(priceable?.interval === "month")}
              data-mode="monthly"
              role="tab"
              aria-selected={priceable?.interval === "month"}
              aria-disabled={!canChangeSubscription}
              tabIndex={canChangeSubscription ? undefined : -1}
              onClick={preventDisabledNavigation}
              type="button"
            >
              <span className="label-text">Pay monthly</span>{" "}
              <span className="save-badge">Start for less</span>
            </Link>
            <Link
              to="/passes/year/pay"
              data-mode="full"
              role="tab"
              aria-selected={isPayingInFull}
              type="button"
              className={optionClassName(isPayingInFull)}
              aria-disabled={!canChangeSubscription}
              tabIndex={canChangeSubscription ? undefined : -1}
              onClick={preventDisabledNavigation}
            >
              <span className="label-text">Pay in full</span>
              {showUpfrontSaving && (
                <>
                  {" "}
                  <span className="save-badge">
                    {freeMonths ? monthsFree(freeMonths) : "Save"}
                  </span>
                </>
              )}
            </Link>
          </div>
          {isPayingInFull && showUpfrontSaving && (
            <div className="savings-callout" id="passSavingsCallout">
              <span className="tag">
                <svg aria-hidden="true">
                  <use href="#icon-champagne" />
                </svg>
                <span className="copy">
                  {freeMonths ? `Pay for ${12 - freeMonths} months, get 12. ` : ""}
                  You'll save a rather marvellous{" "}
                  <strong>
                    {yearlyQuote.currency}
                    {yearlyQuote.discount_amount}
                  </strong>
                  .
                </span>
              </span>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default Subscription;
